import React, { useState, useEffect, useMemo } from "react";

import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";

import { isEmpty } from "lodash";

import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  TextField,
} from "@mui/material";

// Const
import { defaultAlertInfo } from './const';

// Utils
import { getClientsSendRequestParams } from './util';

import StatusTag from "components/StatusTag";
import TabsField from "components/Tab";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import GlobalAlert from "components/Notification";

import GeneralContent from "./General";
import PortsContent from "./Ports";
import ClientsContent from "./Clients";
import SettingsContent from "./Settings";
import StatisticsContent from "./Statistics";
import ManagedContent from "./Managed";
import DhcpContent from "./Dhcp";

import { DEVICE_ICONS, DEVICE_STATUS_TAG, STATUS_TAG_LABEL } from "const";
import { ReactComponent as DescriptionIcon } from "assets/images/icons/icn_table_cell_Message_white.svg";
import { ReactComponent as ClientsIcon } from "assets/images/icons/icn_table_cell_Clients.svg";
import { ReactComponent as RoutersIcon } from "assets/images/icons/icn_table_cell_AP.svg";
import { ReactComponent as ProxyIcon } from "assets/images/icons/icn_table_cell_proxy_device.svg";

const DevicesContent = ({
  contentKey,
  handleDrawerClose,
  fetchFlag,
  pushPage,
  auth,
  deviceId = null,
  infoData,
  networkData,
  portData,
  routerInternetData = {},
  dhcptData = [],
  managedDevicesData,
  capabilityData,
  fetchTableData,
  wirelessRadioData,
  wirelessNetworkData,
  apRadioData,
  tabCountAp,
  tabCountInv,
  tabCountSw,
  tabCountProxy,
  settingsData,
  currentBalance,
  siteDailyUsage
}) => {
  const theme = useTheme();
  const { dataMap, getData, sendRequest } = useDataSourceManager();

  const [orgStatusUuid, _setOrgStatusUuid] = useState(uuidv4());
  const [siteStatusUuid, _setSiteStatusUuid] = useState(uuidv4());
  const currrentUuid =
    auth.scopeLevel === "org" ? orgStatusUuid : siteStatusUuid;

  const [fetchLoading, setFetchLoading] = useState(fetchFlag);
  const [fetchInfoData, setFetchInfoData] = useState({});
  const [fetchNetworkData, setFetchNetworkData] = useState({});
  const [fetchPortData, setFetchPortData] = useState({});
  const [fetchCapabilityData, setFetchCapabilityData] = useState({});
  const [fetchWirelessRadioData, setFetchWirelessRadioData] = useState({});
  const [fetchWirelessNetworkData, setFetchWirelessNetworkData] = useState({});
  const [fetchRouterInternetData, setFetchRouterInternetData] = useState({});
  const [fetchDhcpData, setFetchDhcpData] = useState([]);
  const [fetchApRadioData, setFetchApRadioData] = useState([]);
  const [clientRowData, setClientRowData] = useState([]);

  const [alertInfo, setAlertInfo] = useState(structuredClone(defaultAlertInfo));
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState("");
  const [updateUuid, _setUpdateUuid] = useState(uuidv4());
  const [clientUuid, _setClientUuid] = useState(uuidv4());

  const [tabVal, setTabVal] = useState(null);

  const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs"));
  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
  const realmId = breadcrumbs.realm?.id ?? null;
  const orgsId = breadcrumbs.orgs?.id ?? null;
  const siteId = breadcrumbs.site?.id ?? null;
  let entId = null;
  let orgId = null;

  if (hasManageOrg) {
    entId = realmId;
    if (orgsId) orgId = orgsId;
  } else {
    orgId = realmId;
  }

  useEffect(() => {
    if (fetchFlag) {
      setFetchLoading(true);

      const standalone =
        !auth.orgs.id || Object.keys(auth.orgs).length === 0;
      const baseUrl = standalone
        ? `/orgs/:orgId`
        : `/ents/:entId/orgs/:orgId`;
      const apiConfig = {
        org: `${baseUrl}/devices/:deviceId/status.read`,
        site: `${baseUrl}/sites/:siteId/devices/:deviceId/status.read`,
      };
      const paramsConfig = {
        standalone: {
          org: {
            orgId: auth.realm.id,
            deviceId,
          },
          site: {
            orgId: auth.realm.id,
            deviceId,
            siteId: auth.sites?.id,
          },
        },
        nonStandalone: {
          org: {
            entId: auth.realm.id,
            orgId: auth.orgs.id,
            deviceId,
          },
          site: {
            entId: auth.realm.id,
            orgId: auth.orgs.id,
            siteId: auth.sites?.id,
            deviceId,
          },
        },
      };

      const mode = standalone ? "standalone" : "nonStandalone";
      const restfulParams = paramsConfig[mode][auth.scopeLevel];
      sendRequest(
        apiConfig[auth.scopeLevel],
        currrentUuid,
        restfulParams,
      );
    }
  }, [fetchFlag, deviceId]);

  useEffect(() => {
    if (fetchFlag) {
      const response = getData(currrentUuid);
      if (response && response.data) {
        const data = response.data;
        setFetchInfoData(data.info || {});
        setFetchNetworkData(data.deviceNetwork || {});
        setFetchPortData(data.switchPorts || {});
        setFetchCapabilityData(data.capabilities || {});
        setFetchWirelessRadioData(data["wireless.radio"] || {});
        setFetchWirelessNetworkData(data["wireless.network"] || {});
        setFetchRouterInternetData(data.routerInternet || {});
        setFetchDhcpData(data.dhcpLease || {});
        setFetchApRadioData(data.apRadio || {});
        setFetchLoading(false);
      }
    }
  }, [fetchFlag, dataMap]);

  const currentInfoData = fetchFlag ? fetchInfoData : infoData;
  const currentNetworkData = fetchFlag ? fetchNetworkData : networkData;
  const currentPortData = fetchFlag ? fetchPortData : portData;
  const currentCapabilityData = fetchFlag
    ? fetchCapabilityData
    : capabilityData;
  const currentWirelessRadioData = fetchFlag
    ? fetchWirelessRadioData
    : wirelessRadioData;
  const currentWirelessNetworkData = fetchFlag
    ? fetchWirelessNetworkData
    : wirelessNetworkData;
  const currentSettingNetworkPermissions = {
    ipv4: currentCapabilityData.ipv4Conf,
    ipv6: currentCapabilityData.ipv6Conf,
  };
  const currentRouterInternetData = fetchFlag
    ? fetchRouterInternetData
    : routerInternetData;
  const currentDhcpData = fetchFlag
    ? fetchDhcpData
    : dhcptData;
    const currentApRadioData = fetchFlag
    ? fetchApRadioData
    : apRadioData;

  const isInventoryDevice = auth.scopeLevel === "site" ? false : !currentInfoData.siteName;

  const clientCount = useMemo(() => {
    const deviceType = currentInfoData?.deviceType;
    let count = 0;

    if (deviceType === "ap") {
      count =
        currentWirelessNetworkData?.reduce(
          (acc, network) => acc + (network?.client ?? 0),
          0
        ) ?? 0;
    } else if (deviceType === "switch") {
      count = currentInfoData?.client ?? 0;
    }

    return count;
  }, [currentInfoData?.deviceType, currentWirelessNetworkData, currentInfoData?.client]);

  const tabData = [
    {
      name: "General",
      value: "general",
      disabled: null,
      icon: null,
    },
    ...(currentInfoData.deviceType === "router"
      ? [
        {
          name: "DHCP",
          value: "dhcp",
          disabled: currentInfoData.linkStatus === "offline",
          icon: null,
        },
      ]
      : []),
    ...(currentCapabilityData.tunnelAccess !== true && currentInfoData.deviceType !== "proxy" && currentInfoData.deviceType !== "router"
      ? [
        {
          name:
            (currentInfoData.deviceType === "switch" && "Ports") ||
            (currentInfoData.deviceType === "ap" && "Clients"),
          value:
            (currentInfoData.deviceType === "switch" && "ports") ||
            (currentInfoData.deviceType === "ap" && "clients"),
          disabled: isInventoryDevice,
          icon: null,
        },
      ]
      : []),
    ...(currentInfoData.deviceType === "proxy"
      ? [
        {
          name: "Managed",
          value: "managed",
          icon: null,
        },
      ]
      : []),
    {
      name: "Settings",
      value: "settings",
      disabled: null,
      icon: null,
    },
    ...(currentInfoData.deviceType !== "proxy"
      ? [
        {
          name: "Statistics",
          value: "statistics",
          disabled:
            isInventoryDevice || currentInfoData.linkStatus === "offline",
          icon: null,
        },
      ]
      : []),
  ];

  const fetchClientsData = () => {
    if (currentInfoData.siteId == null) {
      setClientRowData([]);
      return; // for unBound, proxy devices.
    }
    const { apiKey, restfulParams } = getClientsSendRequestParams({ auth, infoData: currentInfoData });

    sendRequest(
      apiKey,
      clientUuid,
      restfulParams
    );
  }

  useEffect(() => {
    if (tabVal === "clients") {
      fetchClientsData();
    }
  }, [tabVal]);

  useEffect(() => {
    setTabVal("general");
  }, [currentInfoData.macAddress]);

  const handleDescriptionsSave = () => {
    const resourcePath =
      hasManageOrg && orgId
        ? `/ents/:entId/orgs/:orgId/devices/:deviceId/settings.update`
        : `/orgs/:orgId/devices/:deviceId/settings.update`;

    const restfulParams =
      hasManageOrg && orgId
        ? { entId, orgId, deviceId: currentInfoData.macAddress }
        : { orgId, deviceId: currentInfoData.macAddress };

    const requestParams = {
      data: {
        info: {
          description: value,
        },
      },
    };

    sendRequest(resourcePath, updateUuid, restfulParams, requestParams);
    setIsEditing(false);
    if (fetchFlag !== true) fetchTableData()
  };

  useEffect(() => {
    setValue(currentInfoData.description || "");
  }, [currentInfoData]);

  useEffect(() => {
    const response = getData(clientUuid);
    const isError = !!response?.code && response?.code !== 200;

    if (response) {
      setFetchLoading(false);

      if (isError) {
        setAlertInfo({
          open: true,
          severity: 'error',
          message: response?.message || 'Failed to get clients.',
        });
      } else {
        const responseData = response?.data ?? null;
        const tmpClientRowData = !isEmpty(response?.data) ? responseData : [];

        setClientRowData(tmpClientRowData);
        setFetchLoading(false);
      }
    }
  }, [dataMap]);

  return (
    <>
      {fetchLoading || Object.keys(currentInfoData).length === 0 ? (
        <Box width="500px">
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              paddingLeft: "25px",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {DEVICE_ICONS[currentInfoData.deviceType][
              currentInfoData.linkStatus
            ] && (
                <img
                  src={
                    DEVICE_ICONS[currentInfoData.deviceType][
                    currentInfoData.linkStatus
                    ]
                  }
                  alt={`${currentInfoData.deviceType}_${currentInfoData.linkStatus}`}
                />
              )}
            <Box sx={{ marginLeft: "10px" }}>
              <Typography
                sx={{
                  color: theme.palette.primary.greyPrimary,
                  fontSize:
                    theme.typography.customFontSize[5],
                  fontWeight:
                    theme.typography.customFontWeight[3],
                }}
              >
                {currentInfoData.deviceName ?? "-"}
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.primary.greyPrimary,
                  fontSize: theme.typography.customFontSize[3],
                  fontWeight: theme.typography.customFontWeight[0],
                }}
              >
                {currentInfoData.deviceType === "proxy"
                  ? currentInfoData.id
                  : currentInfoData.macAddress}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  width: "100%",
                }}
              >
                <StatusTag
                  width="60px"
                  status={currentInfoData.linkStatus}
                  statusMapping={DEVICE_STATUS_TAG}
                  labelMapping={STATUS_TAG_LABEL}
                />
                <Box
                  sx={{
                    width: "58px",
                    color: theme.palette.primary.main,
                    border: `1.5px solid ${theme.palette.primary.main}`,
                    padding: "4px 12px",
                    height: "28px",
                    fontSize:
                      theme.typography.customFontSize[0],
                    borderRadius:
                      theme.shape.customBorderRadius[7],
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    display: "flex",
                  }}
                >
                  {
                    currentInfoData.deviceType === "router" ?
                      <Tooltip arrow title={`Smart APs: ${currentInfoData.smartAPs}`}>
                        <Box
                          sx={{
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            display: "flex",
                          }}
                        >
                          <RoutersIcon />
                          {currentInfoData.smartAPs}
                        </Box>
                      </Tooltip> :
                      currentInfoData.deviceType === "proxy" ?
                        <Box
                          sx={{
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            display: "flex",
                          }}
                        >
                          <ProxyIcon />
                          {tabCountProxy}
                        </Box> :
                        <Tooltip
                          arrow
                          title={`Clients: ${clientCount}`}
                        >
                          <Box
                            sx={{
                              justifyContent: "center",
                              alignItems: "center",
                              textAlign: "center",
                              display: "flex",
                            }}
                          >
                            <ClientsIcon />
                            {clientCount}
                          </Box>
                        </Tooltip>
                  }
                </Box>
                {currentInfoData.deviceType === "proxy"
                  ? ""
                  : <Tooltip
                    placement="bottom"
                    arrow
                    interactive="true"
                    enterDelay={200}
                    leaveDelay={200}
                    title={
                      <Box sx={{ p: 1, width: 260 }}>
                        <Box
                          sx={{
                            fontSize:
                              theme.typography
                                .customFontSize[1],
                            fontWeight:
                              theme.typography
                                .customFontWeight[3],
                            color: theme.palette.primary
                              .white,
                            mb: 1.2,
                          }}
                        >
                          Descriptions
                        </Box>
                        {!isEditing ? (
                          <>
                            <Box
                              sx={{
                                fontSize:
                                  theme.typography
                                    .customFontSize[1],
                                fontWeight:
                                  theme.typography
                                    .customFontWeight[0],
                                color: theme.palette
                                  .primary.white,
                                mb: 1,
                              }}
                            >
                              {value ||
                                "No description."}
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent:
                                  "flex-end",
                              }}
                            >
                              <SolidButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsEditing(
                                    true,
                                  );
                                }}
                                width="100px"
                                height="32px"
                              >
                                Edit
                              </SolidButton>
                            </Box>
                          </>
                        ) : (
                          <>
                            <Box sx={{ width: "100%" }}>
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={value}
                                onChange={(e) =>
                                  setValue(
                                    e.target
                                      .value,
                                  )
                                }
                                inputProps={{
                                  maxLength: 200,
                                }}
                                autoFocus
                                sx={{
                                  "& .MuiInputBase-root":
                                  {
                                    backgroundColor:
                                      theme
                                        .palette
                                        .primary
                                        .white,
                                    borderRadius:
                                      theme
                                        .shape
                                        .customBorderRadius[1],
                                    color: theme
                                      .palette
                                      .primary
                                      .main,
                                  },
                                }}
                              />

                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent:
                                    "flex-end",
                                  mt: 0.5,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="theme.palette.primary.w"
                                >
                                  {value.length} /
                                  200
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent:
                                    "flex-end",
                                  gap: 1,
                                  mt: 1,
                                }}
                              >
                                <OutlineButton
                                  onClick={(
                                    e,
                                  ) => {
                                    e.stopPropagation();
                                    setIsEditing(
                                      false,
                                    );
                                  }}
                                  sx={{
                                    marginRight:
                                      "20px",
                                  }}
                                  width="100px"
                                  height="32px"
                                >
                                  Cancel
                                </OutlineButton>
                                <SolidButton
                                  onClick={
                                    handleDescriptionsSave
                                  }
                                  width="100px"
                                  height="32px"
                                >
                                  Save
                                </SolidButton>
                              </Box>
                            </Box>
                          </>
                        )}
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 28,
                        borderRadius:
                          theme.shape
                            .customBorderRadius[0],
                        backgroundColor:
                          theme.palette.primary.main,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.primary
                              .mainHover,
                        },
                      }}
                    >
                      <DescriptionIcon />
                    </Box>
                  </Tooltip>
                }
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {
              tabVal != null &&
              <TabsField
                tabsStyles={{
                  "& .MuiButtonBase-root": {
                    margin: "0 20px",
                  },
                }}
                tabVal={tabVal}
                handleTabsChange={(e, newValue) => {
                  setTabVal(newValue);
                }}
                tabData={tabData}
              />
            }
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {(tabVal === "general" && (
              <>
                <GeneralContent
                  fetchFlag={fetchFlag}
                  auth={auth}
                  infoData={currentInfoData}
                  isInventoryDevice={isInventoryDevice}
                  networkData={currentNetworkData}
                  capabilityData={currentCapabilityData}
                  portData={currentPortData}
                  wirelessRadioData={currentWirelessRadioData}
                  wirelessNetworkData={
                    currentWirelessNetworkData
                  }
                  fetchTableData={fetchTableData}
                  clientRowData={clientRowData}
                  routerInternetData={currentRouterInternetData}
                  apRadioData={currentApRadioData}
                />
              </>
            )) ||
              (tabVal === "clients" && (
                <ClientsContent
                  auth={auth}
                  infoData={currentInfoData}
                  clientRowData={clientRowData}
                />
              )) ||
              (tabVal === "ports" && (
                <PortsContent
                  auth={auth}
                  infoData={currentInfoData}
                />
              )) ||
              (tabVal === "dhcp" && (
                <DhcpContent
                  dhcpData={currentDhcpData}
                />
              )) ||
              (tabVal === "settings" && (
                <>
                  <SettingsContent
                    auth={auth}
                    capabilityData={currentCapabilityData}
                    infoData={currentInfoData}
                    isInventoryDevice={isInventoryDevice}
                    fetchTableData={fetchTableData}
                    settingNetworkPermissions={
                      currentSettingNetworkPermissions
                    }
                    handleDrawerClose={handleDrawerClose}
                    settingsData={settingsData}
                    currentBalance={currentBalance}
                    siteDailyUsage={siteDailyUsage}
                  />
                </>
              )) ||
              (tabVal === "statistics" && (
                <StatisticsContent
                  auth={auth}
                  infoData={currentInfoData}
                />
              )) ||
              (tabVal === "managed" && (
                <ManagedContent
                  auth={auth}
                  infoData={currentInfoData}
                  managedDevicesData={managedDevicesData}
                  pushPage={pushPage}
                />
              ))}
          </Box>
        </>
      )}

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default DevicesContent;
